import metaApiClient from './meta-client';

export interface CommentAutoReplyTranslation {
    id: number;
    auto_reply_id: number;
    page_id: string;
    reply_message: string | null;
    dm_message: string | null;
    created_at?: string;
    updated_at?: string;
}

export type NegativeMatchType = 'exact' | 'contains';
export type NegativeFilterAction = 'hide' | 'delete';

export interface NegativeWord {
    word: string;
    match_type: NegativeMatchType;
}

export interface CommentAutoReply {
    id: number;
    user_id: number;
    page_ids: string[];
    name: string;
    reply_message: string;
    dm_message: string | null;
    like_comment: boolean;
    hide_comment: boolean;
    auto_translate: boolean;
    funnel_id: number | null;
    trigger_node_id: number | null;
    is_active: boolean;
    // Filtro de palavras negativas
    negative_filter_enabled: boolean;
    negative_filter_action: NegativeFilterAction;
    negative_words: NegativeWord[];
    translations: CommentAutoReplyTranslation[];
    created_at?: string;
    updated_at?: string;
    created_by_name?: string;   // preenchido quando a regra é de um colaborador do partner
}

export interface CommentAutoReplyCreate {
    page_ids: string[];
    name: string;
    reply_message: string;
    dm_message?: string | null;
    like_comment: boolean;
    hide_comment: boolean;
    auto_translate: boolean;
    funnel_id?: number | null;
    trigger_node_id?: number | null;
    negative_filter_enabled?: boolean;
    negative_filter_action?: NegativeFilterAction;
    negative_words?: NegativeWord[];
    translations?: { page_id: string; reply_message?: string; dm_message?: string }[];
}

export interface CommentAutoReplyUpdate {
    page_ids?: string[];
    name?: string;
    reply_message?: string;
    dm_message?: string | null;
    like_comment?: boolean;
    hide_comment?: boolean;
    auto_translate?: boolean;
    funnel_id?: number | null;
    trigger_node_id?: number | null;
    is_active?: boolean;
    negative_filter_enabled?: boolean;
    negative_filter_action?: NegativeFilterAction;
    negative_words?: NegativeWord[];
}

export interface CommentAutoReplyTranslationUpsert {
    page_id: string;
    reply_message?: string | null;
    dm_message?: string | null;
}

const BASE = '/meta/comment-auto-replies';

export const getCommentAutoReplies = async (): Promise<CommentAutoReply[]> => {
    const response = await metaApiClient.get<CommentAutoReply[]>(BASE);
    return response.data;
};

export const getCommentAutoReply = async (id: number): Promise<CommentAutoReply> => {
    const response = await metaApiClient.get<CommentAutoReply>(`${BASE}/${id}`);
    return response.data;
};

export const createCommentAutoReply = async (data: CommentAutoReplyCreate): Promise<CommentAutoReply> => {
    const response = await metaApiClient.post<CommentAutoReply>(BASE, data);
    return response.data;
};

export const updateCommentAutoReply = async (id: number, data: CommentAutoReplyUpdate): Promise<CommentAutoReply> => {
    const response = await metaApiClient.patch<CommentAutoReply>(`${BASE}/${id}`, data);
    return response.data;
};

// Duplica regra + builder; a cópia nasce desativada
export const duplicateCommentAutoReply = async (id: number): Promise<CommentAutoReply> => {
    const response = await metaApiClient.post<CommentAutoReply>(`${BASE}/${id}/duplicate`);
    return response.data;
};

export const deleteCommentAutoReply = async (id: number): Promise<{ message: string }> => {
    const response = await metaApiClient.delete<{ message: string }>(`${BASE}/${id}`);
    return response.data;
};

export const upsertCommentAutoReplyTranslation = async (
    id: number,
    data: CommentAutoReplyTranslationUpsert
): Promise<CommentAutoReply> => {
    const response = await metaApiClient.put<CommentAutoReply>(`${BASE}/${id}/translations`, data);
    return response.data;
};

export const deleteCommentAutoReplyTranslation = async (
    id: number,
    pageId: string
): Promise<{ message: string }> => {
    const response = await metaApiClient.delete<{ message: string }>(`${BASE}/${id}/translations/${pageId}`);
    return response.data;
};
